<div class="pt-1 px-4">
  <label class="mb-0">Rental Dates</label>

  <!-- Single range calendar: first click sets the start date, a later click
       sets the end date. Shown until a full range is chosen (or when editing). -->
  <div class="d-flex justify-content-center mb-2 add-to-cart-calendar" *ngIf="showCalendar">
    <common-date-picker
      [useAsInput]="false"
      [rangeMode]="true"
      [numberOfDaysToBlockOff]="0"
      [blockByHours]="true"
      [minRangeDays]="3"
      [maxRangeDays]="365"
      [maxStartDays]="30"
      #datepicker
      (rangeSelected)="onRangeSelected($event)"
      [fromDate]="startDate"
      [toDate]="endDate"
    ></common-date-picker>
  </div>

  <!-- Duration shortcuts: visible after a start date, hidden once a
       full range is chosen (same as the calendar). -->
  <div class="duration-pills mb-3" *ngIf="showQuickDurationPills" rt="rental-duration-pills">
    <button
      *ngFor="let option of quickDurations"
      type="button"
      class="duration-pill duration-pill-selectable"
      [class.selected]="isQuickDurationSelected(option.days)"
      [attr.aria-pressed]="isQuickDurationSelected(option.days)"
      [attr.aria-label]="'Set rental length to ' + option.label"
      (click)="selectQuickDuration(option.days)"
    >
      <span class="duration-pill-label">{{ option.label }}</span>
      <span class="duration-pill-check" *ngIf="isQuickDurationSelected(option.days)">&check;</span>
    </button>
  </div>

  <!-- Helper hint while the end date is being picked -->
  <div class="text-muted small mb-2 text-center" *ngIf="showCalendar && startDate && !endDate">
    Now select your rental end date (3&nbsp;day minimum, 1&nbsp;year maximum) or pick 1 Week / 2 Weeks / 1 Month / 2 Months / 3 Months
  </div>

  <!-- Show the selected range with an edit affordance when the calendar is collapsed -->
  <div
    class="selected-date-display mb-3"
    *ngIf="!showCalendar && rangeComplete"
    (click)="editDate()"
    role="button"
    tabindex="0"
    (keydown.enter)="editDate()"
    (keydown.space)="editDate(); $event.preventDefault()"
    [attr.aria-label]="getFormattedDate() + ' – ' + getFormattedEndDate() + '. Edit rental dates'"
  >
    <div class="d-flex align-items-center">
      <span class="selected-date-text">{{ getFormattedDate() }} – {{ getFormattedEndDate() }}</span>
      <i class="icon-date-time text-success ml-2 flex-shrink-0" aria-hidden="true"></i>
    </div>
  </div>

  <div
    class="alert alert-warning font-weight-light cursor-pointer d-flex align-items-center mt-3"
    role="alert"
    *ngIf="showBanner"
  >
    <i class="icon-info mr-2"></i>
    <div>
      Orders placed this weekend may not be available for weekend delivery.
    </div>
  </div>

  <!-- Delivery Address (marketplace add-to-cart) or Jobsite ZIP (Admin generate-quote) -->
  <ng-container *ngIf="showDeliveryAddress && !inCheckout">
    <common-location-typeahead-map
      (locationSelected)="onLocationSelected($event)"
      [location]="deliveryAddress"
      [defaultMapLocation]="defaultMapLocation"
      onlyAddresses="true"
      [valid]="!invalidDeliveryAddress"
      [hideMapUntilCoordinates]="true"
      addressText="Delivery Address"
      [isInModal]="true"
    ></common-location-typeahead-map>
  </ng-container>

  <ng-container *ngIf="!showDeliveryAddress && showZipCode && !inCheckout">
    <label class="mb-0">Jobsite ZIP Code</label>
    <input
      type="text"
      [maxLength]="10"
      pattern="\d{5}(-\d{4})?"
      name="zipcode"
      class="form-control"
      [class.border-danger]="invalidZipCode"
      [ngModel]="zipcode"
      (input)="onZipcodeChange($event.target.value)"
    />
    <small *ngIf="invalidZipCode" class="form-text text-red"
      >Invalid zipcode</small
    >
  </ng-container>

  <ng-container *ngIf="showZipCode">
    <!-- Transportation Method Selection -->
    <div class="transport-card-row mt-4 mb-4" *ngIf="!inCheckout">
      <div
        class="transport-card"
        [class.selected]="deliveryOption === DeliveryOptions.Delivery"
        (click)="onDeliveryOptionChange(DeliveryOptions.Delivery)"
      >
        <div class="transport-card-content">
          <i class="icon-flatbed-truck transport-icon"></i>
          <div class="transport-title">We Transport</div>
          <i
            class="icon-checkmark transport-check ml-auto"
            *ngIf="deliveryOption === DeliveryOptions.Delivery"
            aria-hidden="true"
          ></i>
        </div>
      </div>

      <div
        class="transport-card"
        [class.selected]="deliveryOption === DeliveryOptions.PickUp"
        (click)="onDeliveryOptionChange(DeliveryOptions.PickUp)"
      >
        <div class="transport-card-content">
          <i class="icon-profile transport-icon"></i>
          <div class="transport-title">You Transport</div>
          <i
            class="icon-checkmark transport-check ml-auto"
            *ngIf="deliveryOption === DeliveryOptions.PickUp"
            aria-hidden="true"
          ></i>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- Attachments: in-place picker under transportation (do not wait on Azure Search). -->
  <div class="mt-4 mb-4" *ngIf="attachments?.length">
    <common-add-to-cart-attachments
      [machineId]="machineId"
      [machineAccountId]="machineAccountId"
      [attachments]="attachments"
      [attachmentsFromIndex]="attachmentsFromIndex"
      [duration]="duration"
    ></common-add-to-cart-attachments>
  </div>

  <ng-container *ngIf="rpoEnabled">
    <div class="mt-4 mb-4"></div>
    <label>Are you wanting to Rent to Own?</label>
    <div class="form-group">
      <div
        class="d-flex justify-content-between btn-group-toggle"
        data-toggle="buttons"
      >
        <label
          class="btn btn-pass w-100 mr-3 py-2 d-flex align-items-center justify-content-center"
          [class.active]="rpoInterested === true"
        >
          <input
            class="form-check-input"
            type="radio"
            [value]="true"
            (click)="rpoChange(true)"
          />
          <i class="icon-checkmark fs-24 mr-1"></i>
          Yes
        </label>

        <label
          class="btn btn-outline-secondary w-100 py-2 d-flex align-items-center justify-content-center"
          [class.active]="rpoInterested === false"
        >
          <input
            class="form-check-input"
            type="radio"
            [value]="false"
            (click)="rpoChange(false)"
          />
          <i class="icon-close fs-18 mr-1"></i>
          No
        </label>
      </div>
    </div>
  </ng-container>
</div>
